'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: string;
  icon: string;
  title: string;
  description: string;
  benefits: string[];
  amount: string;
}

interface MiniFormData {
  fullName: string;
  amount: string;
  purpose: string;
}

const PersonalFinancingSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedService, setSelectedService] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<MiniFormData>({
    fullName: '',
    amount: '',
    purpose: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
    {
      id: 'mortgage',
      icon: 'HomeIcon',
      title: 'Kredyt hipoteczny',
      description: 'Finansowanie zakupu mieszkania, domu lub działki budowlanej',
      benefits: [
        'Najlepsze oprocentowanie na rynku',
        'Pomoc w wyborze najkorzystniejszej oferty',
        'Wsparcie w całym procesie kredytowym',
      ],
      amount: 'od 100 000 PLN',
    },
  ];

  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId);
    setShowForm(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    setTimeout(() => {
      setSubmitSuccess(false);
      setShowForm(false);
      setSelectedService('');
      setFormData({ fullName: '', amount: '', purpose: '' });
    }, 3000);
  };

  const isFormValid = formData.fullName && formData.amount && formData.purpose;

  if (!isHydrated) {
    return (
      <section id="personal-financing" className="py-16 lg:py-24 bg-background">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Finansowanie osobiste
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Rozwiązania finansowe dopasowane do Twoich potrzeb
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-card rounded-xl h-64 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="personal-financing" className="py-16 lg:py-24 bg-background">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 rounded-full mb-6">
            <Icon name="HomeIcon" size={20} variant="solid" className="text-secondary" />
            <span className="text-sm font-body font-medium text-secondary">
              Kredyt hipoteczny
            </span>
          </div>

          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Kredyt hipoteczny
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Najlepsze warunki finansowania nieruchomości dopasowane do Twoich potrzeb
          </p>
        </div>

        <div className="grid md:grid-cols-1 gap-6 mb-12 max-w-md mx-auto">
          {services.map(service => (
            <div
              key={service.id}
              className={`bg-card rounded-xl shadow-card p-6 transition-all duration-250 hover:shadow-lg cursor-pointer ${
                selectedService === service.id ? 'ring-2 ring-secondary' : ''
              }`}
              onClick={() => handleServiceSelect(service.id)}
            >
              <div className="flex items-center justify-center w-14 h-14 bg-secondary/10 rounded-lg mb-4">
                <Icon name={service.icon as any} size={28} variant="outline" className="text-secondary" />
              </div>

              <h3 className="text-lg font-headline font-bold text-foreground mb-2">
                {service.title}
              </h3>

              <p className="text-sm text-muted-foreground mb-4">
                {service.description}
              </p>

              <div className="space-y-2 mb-4">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Icon name="CheckIcon" size={16} variant="outline" className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-foreground">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-1">Kwota finansowania</p>
                <p className="text-sm font-body font-semibold text-secondary">{service.amount}</p>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleServiceSelect(service.id);
                }}
                className="w-full mt-4 px-4 py-2 bg-secondary text-secondary-foreground font-body font-medium rounded-lg transition-all duration-250 hover:bg-secondary/90"
              >
                Zapytaj o ofertę
              </button>
            </div>
          ))}
        </div>

        {showForm && (
          <div className="bg-card rounded-2xl shadow-card p-8 max-w-2xl mx-auto">
            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mx-auto mb-4">
                  <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-success" />
                </div>
                <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                  Zapytanie wysłane!
                </h3>
                <p className="text-sm text-muted-foreground">
                  Nasz ekspert skontaktuje się z Tobą w ciągu 24 godzin
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
                    Zapytaj o {services.find(s => s.id === selectedService)?.title.toLowerCase()}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Wypełnij formularz, a nasz ekspert przygotuje dla Ciebie najlepszą ofertę
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-body font-medium text-foreground mb-2">
                      Imię i nazwisko *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250"
                      placeholder="Jan Kowalski"
                    />
                  </div>

                  <div>
                    <label htmlFor="amount" className="block text-sm font-body font-medium text-foreground mb-2">
                      Kwota finansowania *
                    </label>
                    <input
                      type="text"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250"
                      placeholder="np. 300 000 PLN"
                    />
                  </div>

                  <div>
                    <label htmlFor="purpose" className="block text-sm font-body font-medium text-foreground mb-2">
                      Cel finansowania *
                    </label>
                    <input
                      type="text"
                      id="purpose"
                      name="purpose"
                      value={formData.purpose}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250"
                      placeholder="np. zakup mieszkania"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setSelectedService('');
                        setFormData({ fullName: '', amount: '', purpose: '' });
                      }}
                      className="flex-1 px-6 py-3 bg-muted text-foreground font-body font-medium rounded-lg transition-all duration-250 hover:bg-muted/80"
                    >
                      Anuluj
                    </button>
                    <button
                      type="submit"
                      disabled={!isFormValid || isSubmitting}
                      className="flex-1 px-6 py-3 bg-accent text-accent-foreground font-body font-semibold rounded-lg shadow-cta transition-all duration-250 hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                          <span>Wysyłanie...</span>
                        </>
                      ) : (
                        'Wyślij zapytanie'
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PersonalFinancingSection;